import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Trash2, Download, CheckCircle, XCircle, UserCheck, UserX, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { OPS_ROLES, INTERNAL_STAFF_ROLES } from '../utils/roles';

/**
 * Composant générique pour les actions bulk sur les entités
 * 
 * @param {string} entityType - Type d'entité (drivers, clients, users, vehicles, pending_drivers)
 * @param {number} selectedCount - Nombre d'éléments sélectionnés
 * @param {function} onClearSelection - Callback pour effacer la sélection
 * @param {function} onDelete - Callback pour supprimer/désactiver
 * @param {function} onActivate - Callback pour activer
 * @param {function} onDeactivate - Callback pour désactiver
 * @param {function} onExport - Callback pour exporter
 * @param {function} onApprove - Callback pour approuver (validations chauffeurs)
 * @param {function} onReject - Callback pour rejeter (validations chauffeurs)
 */
const GenericBulkActionsBar = ({ 
  entityType,
  selectedCount, 
  onClearSelection,
  onDelete,
  onActivate,
  onDeactivate,
  onExport,
  onApprove,
  onReject
}) => {
  const { user } = useAuth();

  if (selectedCount === 0) return null;

  // Configuration par type d'entité (RBAC V2 — OPS_ROLES = back-office logistique, INTERNAL_STAFF_ROLES = tout staff interne)
  const entityConfig = {
    drivers: {
      label: 'chauffeur',
      labelPlural: 'chauffeurs',
      color: 'bg-green-600',
      canDelete: OPS_ROLES,
      canActivate: OPS_ROLES,
      canExport: INTERNAL_STAFF_ROLES
    },
    pending_drivers: {
      label: 'demande',
      labelPlural: 'demandes',
      color: 'bg-amber-600',
      canApprove: OPS_ROLES,
      canReject: OPS_ROLES
    },
    clients: {
      label: 'client',
      labelPlural: 'clients',
      color: 'bg-blue-600',
      canDelete: ['admin'],
      canActivate: OPS_ROLES,
      canDeactivate: OPS_ROLES,
      canExport: INTERNAL_STAFF_ROLES
    },
    users: {
      label: 'utilisateur',
      labelPlural: 'utilisateurs',
      color: 'bg-purple-600',
      canDelete: ['admin'],
      canActivate: ['admin'],
      canDeactivate: ['admin'],
      canExport: ['admin']
    },
    vehicles: {
      label: 'véhicule',
      labelPlural: 'véhicules',
      color: 'bg-teal-600',
      canDelete: OPS_ROLES,
      canActivate: OPS_ROLES,
      canExport: INTERNAL_STAFF_ROLES
    }
  };

  const config = entityConfig[entityType] || entityConfig.drivers;
  const entityLabel = selectedCount > 1 ? config.labelPlural : config.label;

  const canPerform = (action) => {
    const allowedRoles = config[action] || [];
    return allowedRoles.includes(user?.role);
  };

  return (
    <div className="fixed bottom-0 left-64 right-0 bg-white border-t-2 border-[#1db584] shadow-lg z-50">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          <Badge className={`${config.color} text-white text-base px-4 py-2`}>
            ✓ {selectedCount} {entityLabel} sélectionné{selectedCount > 1 ? 's' : ''}
          </Badge>
          
          <div className="flex gap-2">
            {/* Approuver (Validations chauffeurs) */}
            {onApprove && canPerform('canApprove') && (
              <Button
                onClick={onApprove}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approuver tout
              </Button>
            )}

            {/* Rejeter (Validations chauffeurs) */}
            {onReject && canPerform('canReject') && (
              <Button
                onClick={onReject}
                variant="destructive"
                size="sm"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rejeter tout
              </Button>
            )}

            {/* Activer */}
            {onActivate && canPerform('canActivate') && (
              <Button
                onClick={onActivate}
                variant="outline"
                size="sm"
                className="hover:bg-green-50 text-green-600 border-green-300"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Activer
              </Button>
            )}

            {/* Désactiver */}
            {onDeactivate && canPerform('canDeactivate') && (
              <Button
                onClick={onDeactivate}
                variant="outline"
                size="sm"
                className="hover:bg-orange-50 text-orange-600 border-orange-300"
              >
                <UserX className="w-4 h-4 mr-2" />
                Désactiver
              </Button>
            )}

            {/* Supprimer */}
            {onDelete && canPerform('canDelete') && (
              <Button
                onClick={onDelete}
                variant="outline"
                size="sm"
                className="hover:bg-red-50 text-red-600 border-red-300"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {user?.role === 'admin' ? 'Supprimer' : 'Désactiver'}
              </Button>
            )}

            {/* Export */}
            {onExport && canPerform('canExport') && (
              <Button
                onClick={onExport}
                variant="outline"
                size="sm"
                className="hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter CSV
              </Button>
            )}
          </div>
        </div>

        <Button
          onClick={onClearSelection}
          variant="ghost"
          size="sm"
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-4 h-4 mr-1" />
          Annuler la sélection
        </Button>
      </div>
    </div>
  );
};

export default GenericBulkActionsBar;
